function getCookieValue(name) {
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));
  if (!cookie) return null;
  const eq = cookie.indexOf("=");
  try {
    return decodeURIComponent(cookie.slice(eq + 1));
  } catch {
    return cookie.slice(eq + 1);
  }
}

// 清兩次：第一次清當前域名，第二次清跨子網域共用的 cookie
function clearCookie(name) {
  document.cookie = `${name}=; Max-Age=0; path=/`;
  document.cookie = `${name}=; Max-Age=0; path=/; domain=.fantimate.com`;
}

const LOGIN_URL = "https://club.fantimate.com/login";
const API_BASE = "https://new-api.fantimate.com";

async function fetchEligibility(productIds, token) {
  const pids = encodeURIComponent(productIds.join(","));
  try {
    const res = await fetch(
      `${API_BASE}/auth/v1/product-purchase-eligibility/?pids=${pids}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.status === 401) return { ok: false, reason: "unauthorized" };
    if (!res.ok) return { ok: false, reason: "error" };
    return { ok: true, data: await res.json() };
  } catch {
    return { ok: false, reason: "error" };
  }
}

function createEligibilityMap(results) {
  return new Map(
    results.map((r) => [String(r.shopify_unique_id), r.can_purchase]),
  );
}

function makeLoginButton(className, label) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = className;
  btn.textContent = label;
  btn.addEventListener(
    "click",
    () => {
      window.location.href = `${LOGIN_URL}?r=${encodeURIComponent(location.href)}`;
    },
    { once: true },
  );
  return btn;
}

function makeDisabledButton(className, text) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = className;
  btn.disabled = true;
  btn.textContent = text;
  return btn;
}

class AkoAccountAuth extends HTMLElement {
  static #PROFILE_URL = "https://club.fantimate.com/user/profile";

  connectedCallback() {
    const btn = this.querySelector("button");
    if (!btn) return;

    btn.addEventListener("click", () => {
      const token = getCookieValue("auth.access_token");
      if (token) {
        window.location.href = AkoAccountAuth.#PROFILE_URL;
      } else {
        window.location.href = `${LOGIN_URL}?r=${encodeURIComponent(location.href)}`;
      }
    });
  }
}

customElements.define("ako-account-auth", AkoAccountAuth);

// ─── AkoProductAuth ───────────────────────────────────────────────────────────
// 注意：元件採「隱藏不移除」策略
// 驗證通過或非會員商品已登入時，元件保留在 DOM（this.hidden = true）
// 這樣 form guard 的 event listener 才不會被一併移除
// 登出流程會跳轉到外部會員中心再跳回，頁面重新載入，元件會自動重建
// disconnectedCallback 在正常流程中不會觸發，未來加清理邏輯需注意

class AkoProductAuth extends HTMLElement {
  #initialized = false;
  #verified = false;
  #strings = {};

  async connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    const scriptEl = this.querySelector('script[type="application/json"]');
    this.#strings = scriptEl ? JSON.parse(scriptEl.textContent) : {};

    const isMembershipOnly = this.dataset.membershipOnly === "true";
    const productId = this.dataset.productId;

    if (!isMembershipOnly) {
      if (!getCookieValue("auth.access_token")) {
        this.#installLoginGuard();
        this.#showLoginButton();
        return;
      }
      this.#revealForm();
      this.hidden = true;
      return;
    }

    // productId 缺失時顯示無購買權限，避免送出無效 API 請求
    if (!productId) {
      console.warn("[ako-product-auth] missing data-product-id");
      this.#showNoPermissionButton();
      return;
    }

    this.parentElement?.classList.add("ako-membership-product");
    this.#installFormGuard();
    this.#showLoading();

    const token = getCookieValue("auth.access_token");
    if (!token) {
      this.#showLoginButton();
      return;
    }

    const result = await fetchEligibility([productId], token);

    if (!this.isConnected) return;

    if (!result.ok) {
      if (result.reason === "unauthorized") {
        clearCookie("auth.access_token");
        this.#showLoginButton();
      } else {
        this.#showErrorButton();
      }
      return;
    }

    const eligibility = createEligibilityMap(result.data);
    this.#applyResult(eligibility.get(String(productId)) ?? false);
  }

  // 非會員商品：未登入時攔截，直接檢查 token
  #installLoginGuard() {
    this.parentElement
      ?.querySelectorAll('.login-required [name="add"]')
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            if (!getCookieValue("auth.access_token")) e.preventDefault();
          },
          { capture: true },
        );
      });
  }

  // 會員商品：所有路徑都攔截，以 #verified 為準
  #installFormGuard() {
    this.parentElement
      ?.querySelectorAll('.login-required [name="add"]')
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            if (!this.#verified) e.preventDefault();
          },
          { capture: true },
        );
      });
  }

  #applyResult(canPurchase) {
    if (canPurchase) {
      // #verified 只在這裡設為 true，讓驗證來源保持單一
      this.#verified = true;
      this.#revealForm();
      this.hidden = true;
    } else {
      this.#showNoPermissionButton();
    }
  }

  #revealForm() {
    this.parentElement?.querySelectorAll(".login-required").forEach((el) => {
      el.removeAttribute("hidden");
    });
  }

  #showLoading() {
    this.replaceChildren(
      makeDisabledButton(
        "button accent single-add-to-cart-button",
        this.#strings.loading ?? "載入中...",
      ),
    );
  }

  #showLoginButton() {
    this.replaceChildren(
      makeLoginButton(
        "button accent ako-product-login",
        this.#strings.notLoggin ?? "請先登入以繼續購買",
      ),
    );
  }

  #showNoPermissionButton() {
    this.replaceChildren(
      makeDisabledButton(
        "button accent single-add-to-cart-button sold-out",
        this.#strings.membershipRequired ?? "此商品僅限特定等級購買",
      ),
    );
  }

  #showErrorButton() {
    this.replaceChildren(
      makeDisabledButton(
        "button accent single-add-to-cart-button sold-out",
        this.#strings.verifyFailedRefresh ?? "驗證失敗，請重新整理",
      ),
    );
  }
}

customElements.define("ako-product-auth", AkoProductAuth);

class AkoCartAuth extends HTMLElement {
  #initialized = false;
  #verified = false;
  #strings = {};

  async connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    const scriptEl = this.querySelector('script[type="application/json"]');
    this.#strings = scriptEl ? JSON.parse(scriptEl.textContent) : {};

    const token = getCookieValue("auth.access_token");
    const isMembershipOnlyCart = this.dataset.isMembershipOnlyCart === "true";
    const membershipOnlyIds = (this.dataset.membershipOnlyItems || "")
      .split(",")
      .filter(Boolean);

    this.#installCheckoutGuard();

    if (!token) {
      this.#hideCheckout();
      this.#showLoginButton();
      return;
    }

    if (!isMembershipOnlyCart || membershipOnlyIds.length === 0) {
      this.#verified = true;
      this.#revealCheckout();
      return;
    }
    this.#showLoading();

    const result = await fetchEligibility(membershipOnlyIds, token);

    if (!this.isConnected) return;

    if (!result.ok) {
      if (result.reason === "unauthorized") {
        clearCookie("auth.access_token");
        this.#hideCheckout();
        this.#showLoginButton();
      } else {
        this.#hideCheckout();
        this.#showErrorButton();
      }
      return;
    }

    const eligibility = createEligibilityMap(result.data);
    const ineligibleIds = membershipOnlyIds.filter(
      (id) => eligibility.get(id) !== true,
    );

    if (ineligibleIds.length > 0) {
      this.#hideCheckout();
      this.#showBlockedButton(ineligibleIds);
    } else {
      this.#verified = true;
      this.replaceChildren();
      this.#revealCheckout();
    }
  }

  async #removeIneligibleItems(ineligibleIds) {
    try {
      const cartRes = await fetch("/cart.js");
      const cart = await cartRes.json();

      const updates = {};
      cart.items.forEach((item) => {
        if (ineligibleIds.includes(String(item.product_id))) {
          updates[item.id] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        this.#reloadCart();
        return;
      }

      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
    } catch {
      // 清除失敗仍繼續 reload，讓使用者重試
    }
    this.#reloadCart();
  }

  #reloadCart() {
    location.reload();
  }

  #showModal(ineligibleIds) {
    document.querySelector(".ako-modal-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.className = "ako-modal-overlay";

    const modal = document.createElement("div");
    modal.className = "ako-modal";

    const title = document.createElement("p");
    title.className = "ako-modal__title";
    title.textContent =
      this.#strings.cartModalTitle ?? "購物車包含目前會籍無法購買的商品";

    const body = document.createElement("p");
    body.className = "ako-modal__body";
    body.textContent =
      this.#strings.cartModalBody ??
      "點擊確認後，這些商品將會自動從購物車移除。";

    const actions = document.createElement("div");
    actions.className = "ako-modal__actions";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "button";
    cancelBtn.textContent = this.#strings.cancel ?? "取消";
    cancelBtn.addEventListener("click", () => overlay.remove(), { once: true });

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.className = "button accent";
    confirmBtn.textContent = this.#strings.cartModalConfirm ?? "確認並移除商品";
    confirmBtn.addEventListener(
      "click",
      () => {
        cancelBtn.disabled = true;
        confirmBtn.disabled = true;
        confirmBtn.textContent = this.#strings.removing ?? "移除中...";
        this.#removeIneligibleItems(ineligibleIds);
      },
      { once: true },
    );

    actions.append(cancelBtn, confirmBtn);
    modal.append(title, body, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) overlay.remove();
    });
  }

  #installCheckoutGuard() {
    this.#getCheckout()
      ?.querySelectorAll('[name="checkout"]')
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            if (!this.#verified) e.preventDefault();
          },
          { capture: true },
        );
      });
  }

  #revealCheckout() {
    const checkout = this.#getCheckout();
    if (checkout) checkout.hidden = false;
  }

  #hideCheckout() {
    const checkout = this.#getCheckout();
    if (checkout) checkout.hidden = true;
  }

  #getCheckout() {
    return this.parentElement?.querySelector(".proceed-to-checkout") ?? null;
  }

  #showLoading() {
    this.replaceChildren(
      makeDisabledButton(
        "button accent checkout-button full",
        this.#strings.loading ?? "載入中...",
      ),
    );
  }

  #showLoginButton() {
    this.replaceChildren(
      makeLoginButton(
        "button accent checkout-button full ako-cart-login",
        this.#strings.notLoggin ?? "請先登入以繼續購買",
      ),
    );
  }

  #showBlockedButton(ineligibleIds) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "button accent checkout-button full";
    btn.textContent = this.#strings.cartInvalid ?? "購物車包含無法購買的商品";
    btn.addEventListener("click", () => this.#showModal(ineligibleIds));
    this.replaceChildren(btn);
  }

  #showErrorButton() {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "button accent checkout-button full sold-out";
    btn.textContent = this.#strings.verifyFailedRetry ?? "驗證失敗，點擊重試";
    btn.addEventListener("click", () => this.#reloadCart(), { once: true });
    this.replaceChildren(btn);
  }
}

customElements.define("ako-cart-auth", AkoCartAuth);
